/**
 * apps/automations-portal/src/features/admin/LedgerClientThreadsPage.tsx
 *
 * One client's threads — every business flow (an invoice, an enquiry, a
 * quote) as a card with a vertical timeline of what happened on it. This is
 * the detail view the ledger overview links into.
 *
 * The blockId filter's options are the union of every blockId ever seen for
 * this client, accumulated across refetches, so narrowing by block or
 * "needs attention" never makes an option disappear from the dropdown.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { adminApi } from '@/lib/api';
import { formatPence, formatRelativeTime } from '@/lib/format';
import type { LedgerEvent, LedgerSeverity, LedgerThread } from '@/lib/types';

const SEVERITY_VARIANT: Record<LedgerSeverity, 'secondary' | 'warning' | 'success' | 'destructive'> = {
  INFO: 'secondary',
  ACTION: 'warning',
  MONEY: 'success',
  ERROR: 'destructive',
};

const SEVERITY_DOT: Record<LedgerSeverity, string> = {
  INFO: 'bg-muted-foreground',
  ACTION: 'bg-warning',
  MONEY: 'bg-success',
  ERROR: 'bg-destructive',
};

const SINCE_DAYS_OPTIONS = [7, 30, 90] as const;

export function LedgerClientThreadsPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const [threads, setThreads] = useState<LedgerThread[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [blockId, setBlockId] = useState<string | undefined>(undefined);
  const [needsAttention, setNeedsAttention] = useState(false);
  const [sinceDays, setSinceDays] = useState<number>(30);
  const [knownBlockIds, setKnownBlockIds] = useState<string[]>([]);

  useEffect(() => {
    if (!clientId) return;
    setError(null);
    adminApi
      .ledgerClientThreads(clientId, {
        blockId,
        needsAttention: needsAttention ? true : undefined,
        sinceDays,
      })
      .then((data) => {
        setThreads(data);
        setKnownBlockIds((prev) => {
          const seen = new Set(prev);
          for (const thread of data) seen.add(thread.blockId);
          return Array.from(seen).sort();
        });
      })
      .catch(() => setError('Could not load this client’s ledger. Try refreshing the page.'));
  }, [clientId, blockId, needsAttention, sinceDays]);

  if (!clientId) return null;

  const sortedThreads = threads
    ? [...threads].sort((a, b) => new Date(b.lastEventAt).getTime() - new Date(a.lastEventAt).getTime())
    : null;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 p-8">
      <div>
        <h1 className="text-2xl font-semibold">Client ledger</h1>
        <p className="text-sm text-muted-foreground">Every business flow this client&apos;s automations have touched.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={blockId ?? 'all'}
          onValueChange={(next) => setBlockId(next === 'all' ? undefined : next)}
        >
          <SelectTrigger className="w-48" aria-label="Filter by automation">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All automations</SelectItem>
            {knownBlockIds.map((id) => (
              <SelectItem key={id} value={id}>
                {id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={String(sinceDays)} onValueChange={(next) => setSinceDays(Number(next))}>
          <SelectTrigger className="w-40" aria-label="Filter by time range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SINCE_DAYS_OPTIONS.map((days) => (
              <SelectItem key={days} value={String(days)}>
                Last {days} days
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button
          type="button"
          onClick={() => setNeedsAttention((prev) => !prev)}
          aria-pressed={needsAttention}
          className={cn(
            'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
            needsAttention
              ? 'border-warning bg-warning/10 text-foreground'
              : 'border-border text-muted-foreground hover:text-foreground',
          )}
        >
          Needs attention only
        </button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {!error && !threads && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {sortedThreads?.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Nothing matches these filters.
        </div>
      )}

      {sortedThreads && sortedThreads.length > 0 && (
        <div className="flex flex-col gap-4">
          {sortedThreads.map((thread) => (
            <ThreadCard key={thread.threadKey} thread={thread} />
          ))}
        </div>
      )}
    </div>
  );
}

function ThreadCard({ thread }: { thread: LedgerThread }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium">{thread.threadKey}</span>
            <Badge variant="outline">{thread.blockId}</Badge>
            {thread.openApprovals > 0 && <Badge variant="warning">{thread.openApprovals} waiting</Badge>}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{formatPence(thread.valuePence)}</span>
            {thread.minutesSaved > 0 && <span>{thread.minutesSaved} min saved</span>}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {thread.events.length === 0 ? (
          <p className="text-sm text-muted-foreground">No events recorded for this thread yet.</p>
        ) : (
          <ol className="flex flex-col gap-4 border-l border-border pl-4">
            {thread.events.map((event) => (
              <TimelineEvent key={event.id} event={event} />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function TimelineEvent({ event }: { event: LedgerEvent }) {
  return (
    <li className="relative flex flex-col gap-0.5">
      <span
        className={cn('absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full', SEVERITY_DOT[event.severity])}
        aria-hidden="true"
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">{formatRelativeTime(event.occurredAt)}</span>
        <Badge variant={SEVERITY_VARIANT[event.severity]}>{event.severity}</Badge>
      </div>
      <p className="text-sm">{event.summary}</p>
    </li>
  );
}
